import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useFieldPresence, type PresenceTable } from "./field-presence.api";
import {
  FieldPresenceContext,
  type FieldPresenceContextValue,
} from "./field-presence.context";

/**
 * Masquage des cases à cocher sans valeur, dans les drawers d'impression.
 *
 * ## Le principe : un repli ouvert, toujours
 *
 * `isPresent` répond `true` dès qu'on ne *sait pas* : pendant le chargement, en
 * cas d'erreur, si l'API s'est repliée (`degraded`), si l'utilisateur a demandé
 * à tout voir, ou si la clé est absente de la réponse.
 *
 * Ce n'est pas de la prudence gratuite. Les deux erreurs ne se valent pas : un
 * faux « absent » masque une case dont l'utilisateur a besoin et lui fait
 * perdre de la donnée à l'impression ; un faux « présent » affiche une case qui
 * ne produira rien, c'est-à-dire exactement le drawer d'aujourd'hui.
 *
 * C'est pourquoi il n'y a **pas de squelette de chargement** : afficher toutes
 * les cases pendant la requête est le bon état par défaut, pas un pis-aller.
 *
 * ## Une troisième couche, pas un remplacement
 *
 * Les drawers composent déjà deux masquages : la grille de visibilité de la
 * fiche (`isSettingHiddenByFiche`) et la visibilité par onglet
 * (`isPrintSettingTabHidden`). La présence s'ajoute à eux et ne peut que
 * masquer davantage — elle ne réaffiche jamais un réglage qu'ils ont écarté.
 */

type ProviderProps = {
  table: PresenceTable;
  /** Enregistrements sélectionnés. Une liste vide désactive le calcul. */
  ids: string[];
  /** Le drawer est ouvert : inutile d'interroger l'API sinon. */
  enabled?: boolean;
  children: ReactNode;
};

export function FieldPresenceProvider({
  table,
  ids,
  enabled = true,
  children,
}: ProviderProps) {
  // Toujours false à l'ouverture : filtrer les champs vides par défaut
  // (œuvres, multiples, tous modèles). L'utilisateur peut réactiver le
  // bascule pour la session du drawer, sans mémoire entre ouvertures.
  const [showAllFields, setShowAllFieldsState] = useState(false);

  const setShowAllFields = useCallback((value: boolean) => {
    setShowAllFieldsState(value);
  }, []);

  // Pas de requête quand l'utilisateur a demandé à tout voir : la réponse ne
  // changerait rien à l'affichage.
  const shouldQuery = enabled && !showAllFields;
  const { data, isPending, isError } = useFieldPresence(table, ids, shouldQuery);

  const value = useMemo<FieldPresenceContextValue>(() => {
    const filtering =
      shouldQuery &&
      ids.length > 0 &&
      !isError &&
      data != null &&
      !data.degraded;

    return {
      isPresent: (settingKey: string) => {
        if (!filtering) return true;
        // Clé inconnue de l'API : le back-office et l'API ne portent pas
        // exactement les mêmes réglages. On propose, on ne masque pas.
        return data.present[settingKey] !== false;
      },
      isLoading: shouldQuery && ids.length > 0 && isPending,
      showingEverything: !filtering,
      showAllFields,
      setShowAllFields,
    };
  }, [
    shouldQuery,
    ids.length,
    isError,
    data,
    isPending,
    showAllFields,
    setShowAllFields,
  ]);

  return (
    <FieldPresenceContext.Provider value={value}>
      {children}
    </FieldPresenceContext.Provider>
  );
}
